export type ImageSize = 'ORIGINAL' | 'DESKTOP' | 'TABLET' | 'MOBILE';

export interface Image {
    size: ImageSize;
    url: string;
}

/**
 * Map image size to width descriptor for srcset
 * Uses actual image widths from backend:
 * - MOBILE: 640px
 * - TABLET: 1024px
 * - DESKTOP: 1280px
 * - ORIGINAL: typically larger, using 1920w as estimate
 * @param size - Image size key (MOBILE, TABLET, DESKTOP, ORIGINAL)
 * @returns Width descriptor string (e.g. "640w") or empty string
 */
const getWidthDescriptor = (size: ImageSize): string => {
    const widthMap: Record<ImageSize, string> = {
        MOBILE: '640w',
        TABLET: '1024w',
        DESKTOP: '1280w',
        ORIGINAL: '1920w', // Estimate - actual size may vary
    };
    return widthMap[size] || '';
};

/**
 * Build srcset string with width descriptors
 * Browser will automatically select the best image based on viewport and device pixel ratio
 * Note: ORIGINAL is excluded from srcset - it should only be used as fallback in src attribute
 * @param images - Array of images with size and url
 * @returns Formatted srcset string (e.g., "url1 640w, url2 1024w, url3 1280w")
 */
export function buildSrcset(images: Array<{ size: ImageSize; url: string }>): string {
    if (!images || images.length === 0) {
        return '';
    }

    // Filter out ORIGINAL images - they should only be used as fallback in src attribute
    // Sort remaining images by size order: MOBILE -> TABLET -> DESKTOP
    const filteredImages = images.filter((img) => img.size !== 'ORIGINAL');

    if (filteredImages.length === 0) {
        return '';
    }

    const sortedImages = [...filteredImages].sort((a, b) => {
        const order: Record<ImageSize, number> = {
            MOBILE: 1,
            TABLET: 2,
            DESKTOP: 3,
            ORIGINAL: 99,
        };
        return (order[a.size] || 99) - (order[b.size] || 99);
    });

    return sortedImages
        .map((img) => {
            const width = getWidthDescriptor(img.size);
            return width ? `${img.url} ${width}` : img.url;
        })
        .join(', ');
}
